import CanvasSettingsState from "@/stores/CanvasSettingsState";
import UserState from "@/stores/UserState";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import router, { noAuthRoutePrefix } from "@/router";
import log from "loglevel";

/**
 * Codes returned by the API (field `code` of the error body) when the
 * credentials themselves are the problem. Any other 401 — a gateway answering
 * for the API, a backend hiccup, an older backend without codes — must NOT
 * destroy the session: the token is most likely still valid.
 */
export enum AuthErrorCode {
  INVALID_TOKEN = "INVALID_TOKEN",
  USER_NOT_FOUND = "USER_NOT_FOUND",
  USER_DISABLED = "USER_DISABLED",
}

const SESSION_ENDING_CODES = Object.values(AuthErrorCode) as string[];

/** Probe in flight, so a burst of parallel 401s only triggers one check. */
let pendingProbe: Promise<boolean> | null = null;
let sessionEnded = false;

/** Called on login, so a fresh session can be ended again if needed. */
export function resetSessionGuard() {
  sessionEnded = false;
}

function isOnAuthenticatedRoute(): boolean {
  return !new RegExp(`^${noAuthRoutePrefix}`).test(window.location.pathname);
}

function extractCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

function endSession(reason: string) {
  if (sessionEnded) return;
  sessionEnded = true;
  log.warn(`Session ended: ${reason}`);
  CanvasSettingsState.reset();
  UserState.reset();
  $ls.removeItem(LocalStorageKey.JWT);
  router.replace({ name: "welcome" });
}

/**
 * Asks the API whether the stored token is really rejected, bypassing ky (and
 * therefore this very hook) to avoid any recursion. Only a clear-cut 401 counts
 * as a confirmation: a network failure or a 5xx means "we don't know", and we
 * keep the session.
 */
async function isTokenRejected(jwt: string): Promise<boolean> {
  const baseUrl = (import.meta.env.DR_API_URL ?? "").replace(/\/+$/, "");
  try {
    const response = await fetch(`${baseUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return response.status === 401;
  } catch {
    return false;
  }
}

/**
 * Decides what a 401 means for the session. Called for every 401 received by
 * the API client.
 */
export async function handleUnauthorized(body: unknown): Promise<void> {
  if (!isOnAuthenticatedRoute()) return;

  const jwt = $ls.getItem(LocalStorageKey.JWT);
  if (!jwt) {
    // Nothing to invalidate: we are simply browsing a private page while
    // logged out.
    endSession("no token stored");
    return;
  }

  const code = extractCode(body);
  if (code) {
    if (SESSION_ENDING_CODES.includes(code)) endSession(code);
    else log.warn(`401 with a non session-ending code (${code}), session kept`);
    return;
  }

  // Unqualified 401: confirm before logging the user out.
  if (!pendingProbe) {
    pendingProbe = isTokenRejected(jwt).finally(() => {
      pendingProbe = null;
    });
  }
  const rejected = await pendingProbe;

  if (rejected) endSession("unqualified 401 confirmed by auth/me");
  else log.warn("Unqualified 401 not confirmed by auth/me, session kept");
}
